import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, ChevronDown, ChevronUp } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Switch } from '@/components/ui/switch'
import { useTranslation } from '@/i18n'

import { getStoredConsent, storeConsent, announceCookieBanner } from '@/lib/cookieConsent'

export function CookieBanner() {
  const [visible, setVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    const existing = getStoredConsent()
    if (!existing) setVisible(true)
  }, [])

  // One announcement point for every way the banner can open or close, so the
  // support widget never has to guess which handler ran.
  useEffect(() => {
    announceCookieBanner(visible)
  }, [visible])

  const handleAcceptAll = () => {
    storeConsent({ necessary: true, analytics: true, marketing: true, timestamp: new Date().toISOString() })
    setVisible(false)
  }

  const handleRejectNonEssential = () => {
    storeConsent({ necessary: true, analytics: false, marketing: false, timestamp: new Date().toISOString() })
    setVisible(false)
  }

  const handleSavePreferences = () => {
    storeConsent({ necessary: true, analytics, marketing, timestamp: new Date().toISOString() })
    setVisible(false)
  }

  const handleReopen = () => {
    const existing = getStoredConsent()
    if (existing) {
      setAnalytics(existing.analytics)
      setMarketing(existing.marketing)
    }
    setShowPreferences(false)
    setVisible(true)
  }

  return (
    <>
      <AnimatePresence>
        {!visible && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={handleReopen}
            className="fixed bottom-4 left-4 z-[200] min-h-11 flex items-center gap-2 bg-card/90 backdrop-blur-md border border-border rounded-full px-4 py-2 text-xs text-muted-foreground hover:text-foreground shadow-lg hover:shadow-xl transition-all cursor-pointer"
            aria-label={t.cookieBanner.cookieSettings}
          >
            <Cookie className="w-4 h-4" />
            <span className="hidden sm:inline">{t.cookieBanner.cookieSettings}</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {visible && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              // pointer-events-none is load-bearing: this layer spans the whole
              // viewport above the header (z-110) and the support FAB (z-199).
              // With pointer events on it swallowed every click on the site —
              // 32 of 37 controls on /, 69 of 74 on /contact, and the Sign in
              // button on /login — for anyone who had not yet chosen. It dims
              // only; the panel below owns every interaction.
              className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[200] pointer-events-none"
            />

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed bottom-0 left-0 right-0 z-[201] p-4 sm:p-6"
            >
              {/* Height is load-bearing, not cosmetic. Stacked (heading over
                  description over three full-width buttons) this panel measured
                  318px and sat exactly on the hero CTA row, so "Book a
                  Consultation", "Explore Services", "Sign in" and "Send" were
                  all covered at 1440x900 and 375x812 even once the backdrop
                  stopped swallowing clicks. Text and actions share a row from lg
                  up to keep the panel short enough to clear them. */}
              <div className="max-w-6xl mx-auto bg-background/100 backdrop-blur-none border border-border rounded-2xl shadow-2xl p-5 sm:p-6" style={{ backgroundColor: 'var(--background)' }}>
                <div className="lg:flex lg:items-center lg:gap-6">
                  <div className="lg:flex-1">
                    <h3 className="text-base font-bold text-foreground mb-1">{t.cookieBanner.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4 lg:mb-0 leading-relaxed">
                      {t.cookieBanner.description}
                    </p>
                  </div>

                  {/* 2-up on phones: stacked, the three buttons alone cost 152px
                      of an 812px viewport. */}
                  <div className="grid grid-cols-2 gap-2.5 sm:flex sm:flex-row lg:shrink-0">
                  <button onClick={handleAcceptAll} className="flex-1 min-h-11 bg-primary text-primary-foreground font-semibold py-2.5 px-5 rounded-lg hover:opacity-90 transition-opacity text-sm cursor-pointer">
                    {t.cookieBanner.acceptAll}
                  </button>
                  <button onClick={handleRejectNonEssential} className="flex-1 min-h-11 bg-secondary text-secondary-foreground font-semibold py-2.5 px-5 rounded-lg hover:opacity-90 transition-opacity text-sm cursor-pointer">
                    {t.cookieBanner.rejectNonEssential}
                  </button>
                  <button onClick={() => setShowPreferences(!showPreferences)} className="col-span-2 sm:col-span-1 flex-1 min-h-11 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground font-semibold py-2.5 px-5 rounded-lg hover:opacity-90 transition-opacity text-sm cursor-pointer">
                    {t.cookieBanner.managePreferences}
                    {showPreferences ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  </div>
                </div>

                <AnimatePresence>
                  {showPreferences && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-border mt-5 pt-4 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-foreground">{t.cookieBanner.strictlyNecessary}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{t.cookieBanner.strictlyNecessaryDesc}</p>
                          </div>
                          <Switch checked disabled className="opacity-60" />
                        </div>

                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-foreground">{t.cookieBanner.analytics}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{t.cookieBanner.analyticsDesc}</p>
                          </div>
                          <Switch checked={analytics} onCheckedChange={setAnalytics} />
                        </div>

                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-foreground">{t.cookieBanner.marketing}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{t.cookieBanner.marketingDesc}</p>
                          </div>
                          <Switch checked={marketing} onCheckedChange={setMarketing} />
                        </div>

                        <button onClick={handleSavePreferences} className="w-full bg-primary text-primary-foreground font-semibold py-2.5 rounded-lg hover:opacity-90 transition-opacity text-sm cursor-pointer">
                          {t.cookieBanner.savePreferences}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
                  {t.cookieBanner.footerNote}{' '}
                  <Link to="/cookie-policy" className="underline hover:text-foreground transition-colors">{t.cookieBanner.cookiePolicyLink}</Link>{' '}
                  {t.cookieBanner.footerAnd}{' '}
                  <Link to="/privacy-policy" className="underline hover:text-foreground transition-colors">{t.cookieBanner.privacyPolicyLink}</Link>{' '}
                  {t.cookieBanner.footerSuffix}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
